import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { castDraft } from 'immer';

import { AnalysisSet } from '@paragon/contract-library';
import { componentDesignsDeletedWithInsertions } from '@paragon/layout-models';

import pushUniqByGuid from '../../library/utilities/pushUniqByGuid';
import { startup } from './applicationSlice';
import { __UNSAFE_designItemsSelected, __UNSAFE_layoutItemsSelected, clearSelection } from './selectionsSlice';

interface MutableAnalysisSetStore {
  activeAnalysisSetGuid: string | undefined;
  sets: ReadonlyArray<AnalysisSet>;
  loadingAnalysisSet: boolean;
}

export type AnalysisSetStore = Readonly<MutableAnalysisSetStore>;

const initialState: AnalysisSetStore = {
  activeAnalysisSetGuid: undefined,
  sets: [],
  loadingAnalysisSet: false,
};

const analysisSetsSlice = createSlice({
  name: 'analysisSets',
  initialState,
  reducers: {
    analysisSetsSetActiveGuid: (state, action: PayloadAction<string | undefined>) => {
      state.activeAnalysisSetGuid = action.payload;
    },
    analysisSetsPush: (state, action: PayloadAction<AnalysisSet>) => {
      state.sets = castDraft(pushUniqByGuid(state.sets, action.payload));
    },
    toggleLoadingAnalysis: (state) => {
      state.loadingAnalysisSet = !state.loadingAnalysisSet;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(componentDesignsDeletedWithInsertions, (state, action) => {
      if (state.activeAnalysisSetGuid == null) return;

      const activeAnalysisSetComponentDesignGuid = state.sets.find((set) => set.guid === state.activeAnalysisSetGuid)
        ?.componentDesign.guid;

      if (activeAnalysisSetComponentDesignGuid && action.payload.guids.includes(activeAnalysisSetComponentDesignGuid)) {
        state.activeAnalysisSetGuid = undefined;
        state.sets = state.sets.filter((set) => set.componentDesign.guid !== activeAnalysisSetComponentDesignGuid);
      }
    });

    builder.addCase(startup, (state, action) => {
      if (action.payload.selectedAnalysisSetGuid) {
        state.activeAnalysisSetGuid = action.payload.selectedAnalysisSetGuid;
      }
    });

    builder.addCase(__UNSAFE_layoutItemsSelected, (state) => {
      state.activeAnalysisSetGuid = undefined;
    });

    builder.addCase(clearSelection, (state) => {
      state.activeAnalysisSetGuid = undefined;
    });

    builder.addCase(__UNSAFE_designItemsSelected, (state, action) => {
      state.activeAnalysisSetGuid = action.payload.activeAnalysisSetGuid;
    });
  },
});

export const { analysisSetsSetActiveGuid, analysisSetsPush, toggleLoadingAnalysis } = analysisSetsSlice.actions;
export const analysisSets = analysisSetsSlice.reducer;
